import { HandBack, Refusal, ecId, insertRoll, record, scalar, upper, type JanusCtx, type Row } from '../ability/context.js';
import { applyStatusEffect } from '../ability/statuses/apply-status-effect.js';
import { boundaryVerdict, type GmBoundaryVerdict } from '../statuses/gm-boundary.js';
import { encounterCreatureField, encounterField, layerListEntryUuid } from '../../shared/array-types.js';
import { ARCANE_FATIGUE_ID, arcaneFatigueStacks, holdsActiveInCombatSpellPrep, resolveSwitch, type Verdict } from './prepared-spells.js';
import { poolKeyOf } from './spell-prep-table.js';
import { creatureIsInActiveCombat } from '../../roll-init/client-combat-membership.js';

export interface SpellListSwitchPlan extends GmBoundaryVerdict { catalog: Record<string, Row> }

/** The same membership rule as ft_creature_in_combat, including old encounters without combat tags. */
export function spellPrepInCombat(encounter: Row | null, caster: Row): boolean {
    return !!encounterCreatureField(caster.encounter_creatures, 'encounter_id') && creatureIsInActiveCombat(encounter, caster as never);
}

/** The persisted PC attachment or encounter owner supplies the campaign used by the saver. */
export function spellPrepHasCampaign(caster: Row, encounter: Row | null): boolean {
    if (layerListEntryUuid(record(caster.player_characters)?.attached_campaign_id ?? null)) return true;
    return String(encounterField(encounter, 'encounter_master_parent_type') ?? '').toLowerCase() === 'campaign'
        && !!encounterField(encounter, 'encounter_master_parent_id');
}

export function spellListSwitchGate(ctx: Pick<JanusCtx, 'encounter' | 'read'>, caster: Row): Verdict {
    const passives = record(record(record(caster.computed)?.creature)?.abilities)?.passive_ability;
    const threshold = scalar(ctx.read.value(caster, 'arcane_threshold'));
    return resolveSwitch({
        inCombat: spellPrepInCombat(ctx.encounter, caster),
        canPrepareInCombat: holdsActiveInCombatSpellPrep([
            ...(Array.isArray(passives) ? passives : []), ...ctx.read.entries(caster, 'passive_ability'),
        ], ctx.read.definition),
        fatigueStacks: arcaneFatigueStacks(ctx.read.statuses(caster)),
        arcaneThreshold: threshold == null || threshold === '' ? null : Number(threshold),
    });
}

/** One switch, its log, and the complete boundary writes from fatigue and gain hooks. No server rule dispatch. */
export function planSpellListSwitch(ctx: JanusCtx, casterId: string, list: number, classNumber = 1): SpellListSwitchPlan {
    ctx.saveBoundaryInventory = true;
    ctx.saveObjectDamage = true;
    ctx.objectDamage ??= [];
    const caster = ctx.members.get(upper(casterId));
    if (!caster) throw new HandBack('spell-preparation:caster-not-held');
    const gate = spellListSwitchGate(ctx, caster);
    if (!gate.ok) throw new Refusal(gate.message);
    const classId = poolKeyOf(caster, Math.max(1, Math.min(20, classNumber || 1))).split(':')[0]!;
    insertRoll(ctx, { roll_type_1: 'prepare_spell_list', roll_item_id: classId || null, roll_result: list, by: caster, to: null });
    if (spellPrepHasCampaign(caster, ctx.encounter)) {
        const definition = ctx.read.definition(ARCANE_FATIGUE_ID);
        if (!definition) throw new HandBack('apply_status_effect:definition-not-held');
        applyStatusEffect(ctx, { abilityId: ARCANE_FATIGUE_ID, casterId: ecId(caster), targetIds: [ecId(caster)],
            rawDiceMod: 0, rawDiceAdvDisStacks: 0, useAbilityVariable1: null, useAbilityVariable2: null, castingTargets: '' },
        definition, ARCANE_FATIGUE_ID, 'self_status_effect_applied', caster, caster,
        { ends: '0-0-0-0-0-0', flags: [0, 0, 0, 0] }, true, true, 0, false, null);
    }
    if (ctx.deathTail?.length || ctx.concentrationBreaks?.length || ctx.turnTransition || ctx.summons?.length)
        throw new HandBack('gm-boundary:follow-up-persistence-not-modeled');
    const verdict = boundaryVerdict(ctx);
    for (const status of verdict.statusesAdded) status.status_id ??= ctx.read.uuid();
    const catalog: Record<string, Row> = {};
    for (const status of [...verdict.statusesAdded, ...verdict.statusStacks]) {
        const definition = ctx.read.definition(status.status_item_id);
        if (definition) catalog[status.status_item_id] = definition;
    }
    return { ...verdict, catalog };
}
